import {
    AnimationBaseController,
    IAnimationControllerConfiguration,
    Inject,
    SoundType,
} from '@denishussar/html5-game-engine';
import { AnimationContents } from '../../../AnimationContents';
import { ButtonController } from '../../Common/ButtonController';
import { SoundConstants, SoundLevels } from '../../Constants/SoundConstants';
import { GameLogic } from '../../Logic/GameLogic';
import { AppStateEnum, GameModel } from '../../Models/GameModel';
import { CharacterScreenHatController } from './CharacterScreenHatController';
import { CharacterScreenPersonController } from './CharacterScreenPersonController';
import { CharacterScreenWearController } from './CharacterScreenWearController';

export interface ICharacterScreenControllerOptions {}

export class CharacterScreenController extends AnimationBaseController {
    public $model: GameModel;

    private _menuBgWidth: number = 0;
    private _menuBgHeight: number = 0;

    private _personFrameLabel: string[] = ['man1', 'man2', 'woman1', 'woman2'];
    private _personIcons: number = 4;
    private _wearIcons: number = 2;
    private _hatIcons: number = 3;

    private _currentPerson: number = 0;
    private _currentWear: number = 0;
    private _currentHat: number = 2;

    public constructor(
        @Inject private _options: ICharacterScreenControllerOptions,
        @Inject private _gameLogic: GameLogic
    ) {
        super('CharacterScreenController');
    }

    protected getConfiguration(): IAnimationControllerConfiguration {
        return <IAnimationControllerConfiguration>{
            animationContent: AnimationContents.CHARACTER_SCREEN_JSON,
            libraryName: 'character_dialog',
            sounds: [
                {
                    id: SoundConstants.BUTTON_CLICK,
                    type: SoundType.SFX
                }
            ],
        };
    }

    private setState(frameLabel: string): void {
        this.removeChildControllerByName('btn_back');
        this.removeChildControllerByName('btn_continue');
        this.removeChildControllerByName('btn_play');

        for (let i = 0; i < this._personIcons; i++) {
            this.removeChildControllerByName(`person_icon${i}`);
        }

        for (let i = 0; i < this._hatIcons; i++) {
            this.removeChildControllerByName(`hat_icon${i}`);
        }

        for (let i = 0; i < this._wearIcons; i++) {
            this.removeChildControllerByName(`wear_icon${i}`);
        }

        this.gotoAndStopByQuery('character_container', frameLabel)

        switch (frameLabel) {
            case 'character_choice':
                this.addChildController({
                    instanceName: 'character_container.character_choice.btn_continue',
                    controller: ButtonController,
                    model: this.$model,
                    options: {
                        onClick: () => {
                            this.setState('clothes_choice');
                        }
                    }
                }, 'btn_continue');

                for (let i = 0; i < this._personIcons; i++) {
                    this.addChildController({
                        instanceName: `character_container.character_choice.icons_person_choice.icon${i + 1}`,
                        controller: CharacterScreenPersonController,
                        model: this.$model,
                        options: {
                            frameLabel: this._personFrameLabel[i],
                            onClick: () => {
                                this.soundManager.playSound(SoundConstants.BUTTON_CLICK, 0, SoundLevels.UI_ACTION);
                                for (let j = 0; j < this._personIcons; j++) {
                                    this.gotoAndStopByQuery(`character_container.character_choice.icons_person_choice.icon${j + 1}`, 'normal');
                                }
                                this.gotoAndStopByQuery(`character_container.character_choice.icons_person_choice.icon${i + 1}`, 'selected');
                                this._currentPerson = i;
                            }
                        }
                    }, `person_icon${i}`);
                }
                break;

            case 'clothes_choice':
                this.gotoAndStopByQuery('character_container.clothes_choice.icons_person', this._personFrameLabel[this._currentPerson]);

                this.addChildController({
                    instanceName: 'character_container.clothes_choice.btn_back',
                    controller: ButtonController,
                    model: this.$model,
                    options: {
                        onClick: () => {
                            this.setState('character_choice');
                        }
                    }
                }, 'btn_back');

                this.addChildController({
                    instanceName: 'character_container.clothes_choice.btn_continue',
                    controller: ButtonController,
                    model: this.$model,
                    options: {
                        onClick: () => {
                            this.setState('headdress_choice');
                        }
                    }
                }, 'btn_continue');

                for (let i = 0; i < this._wearIcons; i++) {
                    this.addChildController({
                        instanceName: `character_container.clothes_choice.icon_clothes.icon${i + 1}`,
                        controller: CharacterScreenWearController,
                        model: this.$model,
                        options: {
                            onClick: () => {
                                this.soundManager.playSound(SoundConstants.BUTTON_CLICK, 0, SoundLevels.UI_ACTION);
                                this.gotoAndStopByQuery(`character_container.clothes_choice.icon_clothes`, i);
                                this._currentWear = i;
                            }
                        }
                    }, `wear_icon${i}`);
                }
                break;

            case 'headdress_choice':
                this.gotoAndStopByQuery(`character_container.headdress_choice.icons_headdress.headdress_choice_1`, 'normal');
                this.gotoAndStopByQuery(`character_container.headdress_choice.icons_headdress.headdress_choice_2`, 'normal');
                this.gotoAndStopByQuery(`character_container.headdress_choice.icons_headdress.headdress_choice_3`, 'normal');

                this.gotoAndStopByQuery('character_container.headdress_choice.icons_characters', this._getCharacterFrame(this._currentPerson, this._currentWear, 2));
                this.gotoAndStopByQuery(`character_container.headdress_choice.icons_headdress.headdress_choice_3`, 'selected');

                this.addChildController({
                    instanceName: 'character_container.headdress_choice.btn_back',
                    controller: ButtonController,
                    model: this.$model,
                    options: {
                        onClick: () => {
                            this.setState('clothes_choice');
                        }
                    }
                }, 'btn_back');

                this.addChildController({
                    instanceName: 'character_container.headdress_choice.btn_continue',
                    controller: ButtonController,
                    model: this.$model,
                    options: {
                        onClick: () => {
                            this.setState('outcome_choice');
                        }
                    }
                }, 'btn_continue');

                for (let i = 0; i < this._hatIcons; i++) {
                    this.addChildController({
                        instanceName: `character_container.headdress_choice.icons_headdress.headdress_choice_${i + 1}`,
                        controller: CharacterScreenHatController,
                        model: this.$model,
                        options: {
                            onClick: () => {
                                this._currentHat = i;
                                this.soundManager.playSound(SoundConstants.BUTTON_CLICK, 0, SoundLevels.UI_ACTION);
                                for (let j = 0; j < this._personIcons; j++) {
                                    this.gotoAndStopByQuery(`character_container.headdress_choice.icons_headdress.headdress_choice_${j + 1}`, 'normal');
                                }

                                this.gotoAndStopByQuery(`character_container.headdress_choice.icons_headdress.headdress_choice_${i + 1}`, 'selected');
                                this.gotoAndStopByQuery('character_container.headdress_choice.icons_characters', this._getCharacterFrame(this._currentPerson, this._currentWear, this._currentHat));
                            }
                        }
                    }, `hat_icon${i}`);
                }
                break;

            case 'outcome_choice':
                this.gotoAndStopByQuery('character_container.outcome_choice.icons_characters', this._getCharacterFrame(this._currentPerson, this._currentWear, this._currentHat));

                this.addChildController({
                    instanceName: 'character_container.outcome_choice.btn_back',
                    controller: ButtonController,
                    model: this.$model,
                    options: {
                        onClick: () => {
                            this.setState('headdress_choice');
                        }
                    }
                }, 'btn_back');

                this.addChildController({
                    instanceName: 'character_container.outcome_choice.btn_play',
                    controller: ButtonController,
                    model: this.$model,
                    options: {
                        onClick: () => {
                            this._gameLogic.setSkinOptions(this._currentPerson, this._currentWear, [1, 2, 0][this._currentHat]);
                            this._gameLogic.showGameScreen();
                        }
                    }
                }, 'btn_play');
                break;
        }
    }

    protected onInitialize(): void {
        this.setState('character_choice');

        this._menuBgWidth = this.width;
        this._menuBgHeight = this.height;
    }

    protected onResize(width: number, height: number, scale: number) {
        let sizeW = width / scale;
        let sizeH = height / scale;

        this.setScaleByQuery('bg', sizeW / this._menuBgWidth, sizeH / this._menuBgHeight);

        const containerScale = Math.min(sizeW / 1200, sizeH / 1440);

        this.setScaleByQuery('character_container', containerScale, containerScale);
    }

    private _getCharacterFrame(person: number, wear: number, hat: number = 0): number {
        const personFrames = 6;
        const wearFrames = 3;

        return (person * personFrames + wear * wearFrames + hat);
    }
}
